import { supabase } from './supabase';
import { canEvaluateQwen21, QWEN21_MODEL_ID } from './qwenImageModels';
import type { GenerationRequest, GenerationResult } from './imageGeneration';

export async function generateWithQwen21(request: GenerationRequest): Promise<GenerationResult> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !canEvaluateQwen21(session.user.id)) throw new Error('Qwen 2.1 is restricted to private evaluation.');
    if (request.inputImage || request.referenceImages?.length || request.characterReference || request.maskImage) {
        throw new Error('Qwen 2.1 Preview currently supports text-to-image generation.');
    }
    const jobId = crypto.randomUUID();
    const call = async (operation: string) => {
        const { data: { session: current } } = await supabase.auth.getSession();
        if (!current) throw new Error('Sign in to view this generation.');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-qwen`, {
            method: 'POST', signal: AbortSignal.timeout(40_000),
            headers: { Authorization: `Bearer ${current.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation, job_id: jobId, ...(operation === 'start' ? {
                model: QWEN21_MODEL_ID, prompt: request.prompt, aspect_ratio: request.aspectRatio || '1:1', seed: request.seed,
            } : {}) }),
        });
        if (!response.ok) {
            const failure = await response.json().catch(() => ({}));
            const error = new Error(failure.error || 'Qwen image generation failed.') as Error & { status?: number };
            error.status = response.status;
            throw error;
        }
        return response;
    };
    // An uncertain start is recovered by looking up the same UUID, never by
    // resubmitting a fresh image request.
    try { await call('start'); }
    catch (error) {
        if (error instanceof Error && 'status' in error && typeof error.status === 'number' && error.status < 500) throw error;
        await call('status');
    }
    const deadline = Date.now() + 30 * 60_000;
    let pollFailures = 0;
    while (Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        let state;
        try {
            state = await (await call('status')).json();
            pollFailures = 0;
        } catch (error) {
            if (++pollFailures >= 6 || (error instanceof Error && 'status' in error && [401, 403, 404].includes(Number(error.status)))) throw error;
            continue;
        }
        if (state.state === 'failed') throw new Error(state.error || 'Qwen image generation failed.');
        if (state.state === 'succeeded') {
            const blob = await (await call('result')).blob();
            if (blob.type !== 'image/png' || !blob.size) throw new Error('Qwen returned no image.');
            return { imageBlob: blob, model: QWEN21_MODEL_ID, prompt: request.prompt };
        }
    }
    throw new Error('Qwen generation timed out. Check the local worker before trying again.');
}
